/**
 * Weighted monster drop roller.
 *
 * Replaces the previous Phase 1 behavior, where every row in a monster's
 * `drops` array awarded at least once per kill and `weight` was decorative.
 * Phase 4 contract:
 *
 *   - exactly zero or one drop row awards per kill (not "every row wins");
 *   - selection probability is proportional to `weight`;
 *   - awarded qty is sampled uniformly from [qtyMin, qtyMax] inclusive;
 *   - the chosen row is determined by a single rng() draw in [0,1];
 *   - awarded qty is determined by a second rng() draw sampled uniformly
 *     from [qtyMin, qtyMax] inclusive (total RNG consumption: 2 draws
 *     on a non-empty award, 0 draws on an empty drops array);
 *   - an empty drops array awards nothing (no throw);
 *   - behavior is fully driven by the injected rng — production passes
 *     Math.random, tests pass a seeded source for determinism.
 *
 * Pure: no DOM, no React, no I/O. Safe to call from the offline simulation
 * (every rng draw is part of the deterministic replay sequence).
 */
import type { ItemId } from '../state';

export type MonsterDropSpec = {
  readonly itemId: ItemId;
  readonly weight: number;
  readonly qtyMin: number;
  readonly qtyMax: number;
};

export type Rng = () => number;

export type RolledDrop = {
  readonly itemId: ItemId;
  readonly qty: number;
};

/**
 * Roll one monster drop. Returns the winning `{ itemId, qty }` or `null`
 * if the monster declares no rows.
 *
 * Implementation notes:
 *   - rows with weight <= 0 are skipped (defensive — schema requires
 *     `weight > 0`, but a malformed input must not pick a zero-weight row);
 *   - rows with qtyMin > qtyMax are skipped (schema enforces the inverse;
 *     kept defensive for hand-edited catalogues);
 *   - the selector + qty sampler together consume exactly two rng() draws
 *     on a non-empty award path (and zero on an empty drops array), so
 *     `simulate(state, dt, { rng })` replay sequences are preserved across
 *     Phase 4 + future refactors.
 */
export function rollDrop(
  drops: ReadonlyArray<MonsterDropSpec>,
  rng: Rng,
): RolledDrop | null {
  const eligible = drops.filter(
    (d) => d.weight > 0 && d.qtyMin > 0 && d.qtyMax >= d.qtyMin,
  );
  if (eligible.length === 0) return null;

  const totalWeight = eligible.reduce((sum, d) => sum + d.weight, 0);
  if (totalWeight <= 0) return null;

  const draw = rng();
  // Clamp into [0,1) — rng implementations occasionally return exactly 1.
  const normalized = draw >= 1 ? 0.9999999999999999 : draw < 0 ? 0 : draw;
  let threshold = normalized * totalWeight;
  for (const drop of eligible) {
    threshold -= drop.weight;
    if (threshold < 0) {
      const span = drop.qtyMax - drop.qtyMin + 1;
      const qty = drop.qtyMin + Math.floor(rng() * span);
      return { itemId: drop.itemId, qty };
    }
  }
  // Float-rounding fallback: take the last eligible row (preserves
  // determinism without producing a second rng draw).
  const last = eligible[eligible.length - 1]!;
  const span = last.qtyMax - last.qtyMin + 1;
  const qty = last.qtyMin + Math.floor(rng() * span);
  return { itemId: last.itemId, qty };
}
